import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { CheckboxSizes } from '@shared/ui/kit/checkbox/model/types';
import DropdownCheckIcon from '../ui/assets/DropdownCheckIcon';
import { DropdownProps, SelectType } from '../model/types';
import { Checkbox } from '@shared/ui/kit/checkbox';

const useDropdownLogic = <T,>(props: DropdownProps<T>) => {
  const {
    options,
    multiply = false,
    value,
    changeFunc,
    isSearch,
    isDisabled,
    itemText,
    itemValue,
    returnObject,
    isShouldBeClean,
  } = props;

  const [isOpenOptions, setIsOpenOptions] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const [displayedOptions, setDisplayedOptions] = useState(options ? options : []);
  const [inputValue, setInputValue] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    options && setDisplayedOptions(options);
  }, [options]);

  useEffect(() => {
    if (value) {
      if ((multiply && typeof value === 'object' && !Array.isArray(value)) || (!multiply && Array.isArray(value))) {
        return;
      }
      if ((Array.isArray(value) && value.length === 0) || Object.keys(value).length === 0) {
        clearSelectedValues();
        return;
      }

      let displayValue = '';
      let selectedValue: T[] = [];
      if (multiply && Array.isArray(value)) {
        displayValue = value.map((el) => String(el[itemText])).join(', ');
        selectedValue = value;
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        displayValue = String(value[itemText]);
        selectedValue = [value];
      }
      setInputValue([displayValue]);
      setSelectedOptions(selectedValue);
      changeFunc && changeFunc(getValidData(selectedValue));
    }
  }, [value]);

  useEffect(() => {
    if (isShouldBeClean) {
      clearSelectedValues();
    }
  }, [isShouldBeClean]);

  const inputChangeHandler = (e: { target: { value: string } }) => {
    if (!isSearch) return;

    const { value } = e.target;
    setInputValue([value]);

    if (options?.length) {
      if (value) {
        const valuesBeingSearched = options.filter((el) =>
          String(el[itemText]).toLowerCase().includes(value.toLowerCase())
        );
        setDisplayedOptions(valuesBeingSearched);
        setIsOpenOptions(true);
        return;
      }
      setDisplayedOptions(options || []);
      return;
    }
    setInputValue([value]);
  };

  const toggleIsOpen = () => {
    if (isDisabled) return;
    if (!isOpenOptions && inputValue.length === 0) {
      setDisplayedOptions(options || []);
    }
    setIsOpenOptions(!isOpenOptions);
  };

  const cleanInputHandler = () => {
    if (isSearch) setInputValue([]);
  };

  const clearSelectedValues = () => {
    if (isDisabled) return;
    setSelectedOptions([]);
    setInputValue([]);
    setDisplayedOptions(options ? options : []);
    changeFunc && changeFunc([]);
  };

  const toggleSelectOptionHandler = (option: T) => {
    if (multiply) {
      if (checkIsSelectedOption(option)) {
        removeOption(option);
        return;
      }
    }
    if (checkIsSelectedOption(option)) {
      if (isSearch) {
        setInputValue([String(option[itemText])]);
      }
      setIsOpenOptions(!isOpenOptions);
      return;
    }
    addOption(option);
  };

  const getValidData = (data: T[]): T[] | T[keyof T][] => {
    if (returnObject) {
      return data;
    }

    return data.map((el) => (itemValue ? el[itemValue] : el[itemText]));
  };

  const removeOption = (option: T) => {
    const updatedValues = selectedOptions.filter((selectedOption) => selectedOption[itemText] !== option[itemText]);
    changeFunc && changeFunc(getValidData(updatedValues));
    setInputValue(updatedValues.map((el) => String(el[itemText])));
    setSelectedOptions(updatedValues);
  };

  const addOption = (option: T) => {
    if (multiply) {
      const newValues = [...selectedOptions, option];
      changeFunc && changeFunc(getValidData(newValues));
      setInputValue([newValues.map((el) => el[itemText]).join(', ')]);
      setSelectedOptions(newValues);
      return;
    }
    setSelectedOptions([option]);
    setInputValue([String(option[itemText])]);
    changeFunc && changeFunc(getValidData([option]));
    toggleIsOpen();
  };

  const checkIsSelectedOption = (option: T) => {
    return selectedOptions.some((el) => el[itemText] === option[itemText]);
  };

  const getSelectIcon = (selectedType: SelectType, isChecked: boolean): ReactNode | '' => {
    const icons: Record<string, ReactNode> = {
      checkbox: <Checkbox isChecked={isChecked} size={'medium' as CheckboxSizes} onChange={() => {}} />,
      default: isChecked ? <DropdownCheckIcon size={'medium'} /> : '',
    };
    return icons[selectedType];
  };

  const inputStopPrevent = (e: SyntheticEvent, isSearch: boolean) => {
    !isSearch && e.preventDefault();
  };

  const clickOutsideHandler = () => {
    if (isSearch) {
      let newInputValue = '';

      if (selectedOptions.length > 0) {
        newInputValue = selectedOptions.map((el) => el[itemText]).join(', ');
      }
      if (inputValue[0] !== newInputValue) {
        setInputValue([newInputValue]);
      }
      setDisplayedOptions(options || []);
    }
  };

  return {
    isOpenOptions,
    selectedOptions,
    inputValue,
    displayedOptions,
    inputRef,
    inputChangeHandler,
    toggleIsOpen,
    toggleSelectOptionHandler,
    removeOption,
    addOption,
    checkIsSelectedOption,
    setIsOpenOptions,
    getSelectIcon,
    inputStopPrevent,
    clearSelectedValues,
    cleanInputHandler,
    clickOutsideHandler,
  };
};

export default useDropdownLogic;
